import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import Consultation from "@/components/sections/Consultation";
import SEOHead from "@/components/SEOHead";
import { Link } from "@/lib/router-compat";
import {
  RelatedPages,
  allTreatmentsLinks,
  allDecisionLinks,
} from "@/components/RelatedPages";
import { Phone, Brain, Target, Ruler } from "lucide-react";
const AcceleratedTmsFmriTargeting = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={
          "How Brain Mapping Works in SAINT® TMS | Neuro Wellness TMS Centers of America"
        }
        description={
          "SAINT uses an MRI scan to map your brain individually before treatment. Learn how this personalized targeting differs from standard TMS — in plain language. Coral Springs, FL."
        }
        canonical={"/accelerated-tms-fmri-targeting"}
        keywords={
          "fMRI guided TMS, SAINT fMRI targeting, conventional TMS vs SAINT, dorsolateral prefrontal cortex TMS, neuronavigation TMS"
        }
        schemaJson={{
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          name: "How Brain Mapping Works in SAINT TMS",
          description:
            "Explains how fMRI-guided targeting in SAINT differs from conventional TMS scalp-measurement targeting, in patient-friendly language.",
          about: [
            "fMRI",
            "SAINT TMS",
            "Brain Targeting",
            "Transcranial Magnetic Stimulation",
          ],
          provider: {
            "@id": "https://neurowellnesstms.com/#organization",
          },
        }}
      />

      <Navbar />
      <main className="pt-16 sm:pt-20">
        <section className="relative overflow-hidden bg-gradient-navy py-16 sm:py-20 lg:py-24">
          <div className="pointer-events-none absolute -right-32 -top-24 h-96 w-96 rounded-full bg-accent/15 blur-3xl" />
          <div className="container-px mx-auto max-w-4xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-3 py-1.5 text-xs font-medium text-accent sm:px-4 sm:text-sm">
              Patient's Guide · Part 2
            </span>
            <h1 className="mt-6 font-headings text-[2rem] font-extrabold leading-[1.1] text-white sm:text-3xl md:text-4xl lg:text-[2.75rem]">
              How Brain Mapping Works in SAINT
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-primary-foreground/80 sm:text-lg">
              SAINT doesn't guess where to aim. It maps your brain first — and
              that's one of the biggest differences between SAINT and standard
              TMS.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <a
                href="/#consultation"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-accent px-6 py-3 text-base font-semibold text-accent-foreground shadow-glow hover:opacity-90"
              >
                Schedule a Free Phone Consultation
              </a>
              <a
                href="tel:9548279793"
                className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-white/40 bg-transparent px-6 py-3 text-base font-semibold text-white hover:bg-white/10"
              >
                <Phone className="h-5 w-5" />
                Call 954-827-9793
              </a>
            </div>
          </div>
        </section>

        <section className="bg-background py-12 lg:py-16">
          <div className="container-px mx-auto max-w-3xl space-y-10">
            {/* How standard TMS works — simplified */}
            <div className="space-y-4 text-[17px] leading-relaxed text-muted-foreground">
              <h2 className="font-headings text-2xl font-bold text-primary">
                How standard TMS finds its target
              </h2>
              <p>
                Standard TMS aims for a region of the brain involved in mood. To
                find it, a technician usually finds your "motor threshold" — the
                stimulation level that makes your thumb twitch — and then
                measures forward on your scalp by a standard rule.
              </p>
              <p>
                This works, and standard TMS has helped many people. But the
                measurement is based on averages. Every brain is a little
                different, so the coil may end up on a spot that's approximately
                right for you — but not exactly right.
              </p>
            </div>

            {/* What SAINT does differently — simplified */}
            <div className="space-y-4 text-[17px] leading-relaxed text-muted-foreground">
              <h2 className="font-headings text-2xl font-bold text-primary">
                What SAINT does differently
              </h2>
              <p>
                SAINT starts with an MRI scan — about 45 minutes in the scanner.
                The scan shows how different regions of your brain are
                connected.
              </p>
              <p>
                A computer then analyzes that scan to find the exact spot on the
                surface of your brain that has the strongest connection to a
                deeper mood-regulating area. That spot is unique to you — it
                doesn't come from an average. The system then uses that exact
                coordinate for every session.{" "}
                <Link
                  to="/learn/does-saint-tms-require-an-mri"
                  className="text-accent hover:underline"
                >
                  Does SAINT\u00ae require an MRI?
                </Link>
                {" \u2014 and see "}
                <Link
                  to="/learn/beam-f3-vs-fmri-tms"
                  className="text-accent hover:underline"
                >
                  BEAM F3 vs fMRI targeting
                </Link>
                .
              </p>
            </div>

            {/* Three differences — visual cards */}
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-2xl border border-border bg-card p-5 shadow-soft text-center">
                <Brain className="mx-auto h-8 w-8 text-accent" />
                <h3 className="mt-3 font-headings text-base font-bold text-primary">
                  Speed
                </h3>
                <p className="mt-1 text-[15px] leading-relaxed text-muted-foreground">
                  Five days instead of six to nine weeks
                </p>
              </div>
              <div className="rounded-2xl border border-border bg-card p-5 shadow-soft text-center">
                <Target className="mx-auto h-8 w-8 text-accent" />
                <h3 className="mt-3 font-headings text-base font-bold text-primary">
                  Personalized
                </h3>
                <p className="mt-1 text-[15px] leading-relaxed text-muted-foreground">
                  Your brain map, not a population average
                </p>
              </div>
              <div className="rounded-2xl border border-border bg-card p-5 shadow-soft text-center">
                <Ruler className="mx-auto h-8 w-8 text-accent" />
                <h3 className="mt-3 font-headings text-base font-bold text-primary">
                  Precise
                </h3>
                <p className="mt-1 text-[15px] leading-relaxed text-muted-foreground">
                  Computer-guided placement, not scalp measurement
                </p>
              </div>
            </div>

            {/* Honest limits — simplified */}
            <div className="space-y-4 text-[17px] leading-relaxed text-muted-foreground">
              <h2 className="font-headings text-2xl font-bold text-primary">
                What the scan can and can't do
              </h2>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  <span>
                    It doesn't diagnose depression. No MRI can do that. The scan
                    tells us where to aim — it doesn't confirm you have
                    depression or predict whether you'll respond.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  <span>
                    It's a targeting tool, not a magic step. The research behind
                    SAINT comes from small but well-designed studies. The main
                    trial enrolled 29 patients. Those results are striking, and
                    they're also from a small sample.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  <span>
                    No brain scan means it isn't SAINT. A five-day accelerated
                    schedule without MRI mapping may be a perfectly reasonable
                    treatment — but it's a different protocol and shouldn't be
                    called SAINT.
                  </span>
                </li>
              </ul>
            </div>

            {/* Practical questions — simplified */}
            <div className="space-y-4 text-[17px] leading-relaxed text-muted-foreground">
              <h2 className="font-headings text-2xl font-bold text-primary">
                Practical questions about the scan
              </h2>
              <p>
                Where is the scan done — at the clinic or at an imaging center?
                Is it billed separately? How long between the scan and your
                first session? If you have claustrophobia, metal implants, or a
                pacemaker, mention it early — both the scan and the treatment
                have screening requirements.
              </p>
            </div>

            <div className="rounded-2xl border border-light-blue bg-light-blue/30 p-6">
              <h3 className="font-headings text-lg font-bold text-primary">
                Continue reading
              </h3>
              <div className="mt-3 flex flex-col gap-2">
                <Link
                  to="/accelerated-tms-coverage"
                  className="text-accent hover:underline"
                >
                  Part 3: What Insurance Coverage Looks Like →
                </Link>
                <Link
                  to="/accelerated-tms-five-day-week"
                  className="text-accent hover:underline"
                >
                  Part 4: What the Five-Day Week Is Like →
                </Link>
                <Link
                  to="/accelerated-tms-other-conditions"
                  className="text-accent hover:underline"
                >
                  Part 5: Can TMS Help With Other Conditions? →
                </Link>
                <Link
                  to="/find-a-verified-saint-provider"
                  className="text-accent hover:underline"
                >
                  Part 1: How to Verify a Clinic Is Credentialed for SAINT →
                </Link>
              </div>
            </div>
          </div>
        </section>

        <RelatedPages
          heading="Related Resources"
          links={[...allTreatmentsLinks, ...allDecisionLinks]}
        />
        <Consultation />
      </main>
      <Footer />
    </div>
  );
};
export default AcceleratedTmsFmriTargeting;
